import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { IGetStaticProps } from "../../interfaces/next/IGetStaticProps";
import { Stack, ThemeProvider, Typography } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { MaterialLayout } from "../../components/global/layout/material/MaterialLayout";
import { PaymentCardsSection } from "../../components/payment/material/PaymentCardsSection";
import { PaymentCrestsSection } from "../../components/payment/material/PaymentCrestsSection";
import { PaymentTable } from "../../components/payment/material/PaymentTable";
import { Check } from "@mui/icons-material";
import { PaymentSmallCardsSection } from "../../components/payment/material/PaymentSmallCardsSection";
import { useStripePrices } from "../../hooks/global/useStripePrices";
import useUser from "../../hooks/user/useUser";
import { useUpdateUserSubscription } from "../../hooks/trainingPlan/useUpdateUserSubscription";
import { PaymentQASection } from "../../components/payment/material/paymentQASection/PaymentQASection";
import Head from "next/head";
import { useTranslation } from "next-i18next";

export default function Payment() {
  const theme = useSelector((state: RootState) => state.theme);
  const { t } = useTranslation();

  const { user } = useUser();
  console.log(user);
  useUpdateUserSubscription(user, true, true);

  useStripePrices();

  const featuresPlans = [
    {
      title: "Available workouts",
      content: [
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={0}
        >
          Limited selection
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={1}
        >
          2 complete plans per month
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={2}
        >
          3 complete plans per month
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={3}
        >
          5 complete plans per month
        </Typography>,
      ],
    },
    {
      title: "Active plans simultaneously",
      content: [
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={0}
        ></Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={1}
        >
          1 plan (switch anytime)
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={2}
        >
          2 plans (switch anytime)
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={3}
        >
          3 plans (switch anytime)
        </Typography>,
      ],
    },
    {
      title: "Create your own workouts",
      content: [
        <Check
          sx={{
            color: theme.componentColors.primary.primary,
          }}
          key={0}
        />,
        <Check
          sx={{
            color: theme.componentColors.primary.primary,
          }}
          key={1}
        />,
        <Check
          sx={{
            color: theme.componentColors.primary.primary,
          }}
          key={2}
        />,
        <Check
          sx={{
            color: theme.componentColors.primary.primary,
          }}
          key={3}
        />,
      ],
    },
  ];

  const featuresContentSuport = [
    {
      title: "Exclusive content",
      content: [
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={0}
        >
          Limited Access
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={1}
        >
          Access to exclusive content (workouts, articles etc.)
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={2}
        >
          Access to exclusive content (workouts, articles etc.)
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={3}
        >
          Access to exclusive content (workouts, articles etc.)
        </Typography>,
      ],
    },
    {
      title: "Workout support",
      content: [
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={0}
        ></Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={1}
        >
          Direct support from Pod1um
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={2}
        >
          Direct support from Pod1um
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={3}
        >
          Priority support from Pod1um
        </Typography>,
      ],
    },
    {
      title: "Personalised guidance",
      content: [
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={0}
        ></Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={1}
        ></Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={2}
        ></Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={3}
        >
          Personalised guidance from training plan creators
        </Typography>,
      ],
    },
  ];

  const featuresConnectivity = [
    {
      title: "Connect with Wearables/Apps",
      content: [
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={0}
        ></Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={1}
        >
          Basic connectivity
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={2}
        >
          Connect with Apple Health, Google Health, FitBit
        </Typography>,
        <Typography
          sx={{
            color: theme.componentColors.surface.onSurface,
          }}
          key={3}
        >
          Connect with premium wearables (STATSports, Strava)
        </Typography>,
      ],
    },
  ];

  return (
    <ThemeProvider theme={createTheme(theme.materialTheme)}>
      <MaterialLayout>
        <Head>
          <title>{t("membership-plans:membership-plans-title")}</title>
          <meta
            name="dc.title"
            content={t("membership-plans:membership-plans-title")}
          />
          <meta
            name="og:title"
            content={t("membership-plans:membership-plans-title")}
          />
          <meta
            name="twitter:title"
            content={t("membership-plans:membership-plans-title")}
          />
        </Head>

        <Stack className="items-center justify-center space-y-[16px] my-10 md:my-20">
          <Typography
            sx={{
              color: theme.componentColors.surface.onSurfaceDetachable,
            }}
            variant="h1"
            className="text-5xl md_2:text-[4.5rem] lg:text-[5rem] font-bold  text-center lg:leading-[5rem]"
          >
            Elevate your training with a <br className="hidden lg:inline" />{" "}
            Pod1um membership
          </Typography>

          <Typography
            sx={{ color: theme.componentColors.surface.onSurface }}
            className="md_2:text-xl lg:text-2xl text-center "
          >
            Unlock exclusive training resources and expert guidance at a
            fraction of the usual cost.
          </Typography>
        </Stack>

        <PaymentCardsSection />

        <PaymentCrestsSection />

        <div className="mt-10 md:mt-20 mb-6 lg:mb-12">
          <Typography
            sx={{
              color: theme.componentColors.background.onBackground,
            }}
            className="text-2xl md:text-[28px] lg:text-4xl text-left"
          >
            Compare Pod1um plans and choose your path to success
          </Typography>
        </div>

        <PaymentTable
          stickyTitle="Training and Plans"
          height="300px"
          className="pb-20 md:pb-0"
          featuresComponents={featuresPlans}
        />

        <PaymentTable
          stickyTitle="Content and Support"
          height="340px"
          className="pb-48 md:pb-0"
          featuresComponents={featuresContentSuport}
        />

        {/* <PaymentTable
            stickyTitle="Connectivity"
            height="290px"
            featuresComponents={featuresConnectivity}
          /> */}

        <PaymentSmallCardsSection />

        {/* <PaymentTestimonials /> */}

        <PaymentQASection />
      </MaterialLayout>
    </ThemeProvider>
  );
}

export async function getStaticProps({
  locale,
}: {
  locale: string;
}): Promise<IGetStaticProps> {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        "common",
        "menu",
        "footer",
        "membership-plans",
      ])),
      // allBlogs
    },
  };
}
